import React, { useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import textNodes from 'domains/text_nodes';

import type { NodeAPIData } from 'types/catalog_types';
import type { TextNodeStyleProp } from 'types/node_types';
import type { TextAlignment } from 'domains/text_nodes';
import type { Point } from 'types/common_types';

type TextNodeProps = {
  node: NodeAPIData;
  styles: TextNodeStyleProp;
  showWarning: boolean;
  nodeDimension: { w: number; h: number };
  fontSize: number;
  fontFamily: string;
  textColor: string;
  verticalAlign: TextAlignment;
  horizontalAlign: TextAlignment;
  textContent: string;
  autofit: boolean;
  active: boolean;
  onClick?: React.MouseEventHandler;
};

type TextLineProps = {
  fontSize: number | string;
  fontFamily: string;
  textColor: string;
  verticalAlign: TextAlignment;
  horizontalAlign: TextAlignment;
  position: Point & { dy: number };
  children: React.ReactNode;
};

function TextNode({
  node,
  styles,
  showWarning,
  nodeDimension,
  fontSize,
  fontFamily,
  textColor,
  verticalAlign,
  horizontalAlign,
  textContent,
  onClick,
}: TextNodeProps) {
  const svgRef = useRef<SVGSVGElement>(null);

  const textX = useMemo(() => {
    const { w } = nodeDimension;

    switch (horizontalAlign) {
      case 'start':
        return 0;
      case 'end':
        return w;
      case 'middle':
      default:
        return w / 2;
    }
  }, [horizontalAlign, nodeDimension]);

  const lines = useMemo(() => {
    const lineHeight = svgRef.current?.querySelector('text')?.getBoundingClientRect().height || 1;
    return textContent
      .split(/\n\r?/g)
      .filter((l) => !!l)
      .map((line, i, arr) => {
        const nodesHeight = arr.length * lineHeight;
        const offset = lineHeight * i;
        let y = 0;

        /**
         * We are using dy to place each line relative to the previous.
         *
         * This means each line uses the same y position, which is located on the baseline
         * of the first line of text. Offsetting by the line height does not look right,
         * so we offset by the font size. For some text rendering reason, this works
         * pretty well.
         *
         * We had to avoid using the dominant-baseline presentation attribute since the
         * preview rendering library we use on the back-end does not support this property.
         * https://gnome.pages.gitlab.gnome.org/librsvg/devel-docs/features.html
         *
         */

        switch (verticalAlign) {
          case 'middle':
            // height difference btw the middle of the box & text lines, excluding the first line
            y = nodeDimension.h / 2 - nodesHeight / 2 + fontSize;
            break;
          case 'end':
            // height difference btw the container & text lines, excluding the first line
            y = nodeDimension.h - nodesHeight + fontSize;
            break;
          case 'start':
          default:
            // the height of the first line
            y = fontSize;
            break;
        }

        return (
          <TextLine
            key={line + i}
            fontSize={fontSize}
            fontFamily={fontFamily}
            textColor={textColor}
            verticalAlign={verticalAlign}
            horizontalAlign={horizontalAlign}
            position={{ x: textX, y, dy: offset }}
          >
            {line}
          </TextLine>
        );
      });
  }, [
    nodeDimension,
    textContent,
    textColor,
    fontSize,
    fontFamily,
    verticalAlign,
    horizontalAlign,
    textX,
  ]);

  return (
    <React.Fragment>
      <div
        role="textbox"
        tabIndex={0}
        className="text_node--container"
        style={styles.node}
        onClick={onClick}
      >
        {showWarning && (
          <div
            className="animated wobble svg-warning svg-warning__text-overflow"
            title="It appears your text is outside the text box."
          >
            <FontAwesomeIcon icon="exclamation-triangle" />
          </div>
        )}
        <div className="svg-container" style={styles.svg}>
          <svg
            ref={svgRef}
            id={`${textNodes.utils.textNodeSVGBaseId}${node.id}`}
            className="text_node--svg"
            width={nodeDimension.w}
            height={nodeDimension.h}
            xmlns="http://www.w3.org/2000/svg"
          >
            {lines}
          </svg>
        </div>
      </div>
    </React.Fragment>
  );
}

function TextLine(props: TextLineProps) {
  const { horizontalAlign, fontSize, fontFamily, textColor, position } = props;

  return (
    <text
      textAnchor={horizontalAlign}
      fontSize={fontSize}
      fontFamily={fontFamily}
      fill={textColor}
      x={`${position.x}px`}
      y={`${position.y}px`}
      dy={`${position.dy}px`}
    >
      {props.children}
    </text>
  );
}

export default TextNode;
